import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import { getMessaging, isSupported, type Messaging } from "firebase/messaging";
import { RuntimeConfig } from "../runtimeConfig";

/**
 * Firebase client configuration loaded from runtime config.
 */
export const firebaseConfig = {
  apiKey: RuntimeConfig.FIREBASE_API_KEY ?? "",
  authDomain: RuntimeConfig.FIREBASE_AUTH_DOMAIN ?? "",
  projectId: RuntimeConfig.FIREBASE_PROJECT_ID ?? "",
  storageBucket: RuntimeConfig.FIREBASE_STORAGE_BUCKET ?? "",
  messagingSenderId: RuntimeConfig.FIREBASE_MESSAGING_SENDER_ID ?? "",
  appId: RuntimeConfig.FIREBASE_APP_ID ?? "",
};

/**
 * Check whether Firebase can be initialized in the browser.
 */
const hasHydrate =
  typeof window !== "undefined" && Object.entries(RuntimeConfig).length > 0;

/**
 * Firebase app instance for the current browser session.
 */
const app: FirebaseApp | undefined = hasHydrate
  ? getApps().length
    ? getApp()
    : initializeApp(firebaseConfig)
  : undefined;

let messagingPromise: Promise<Messaging | undefined> | null = null;

/**
 * Firebase Messaging instance, resolved lazily.
 * Resolves to `undefined` during SSR, when runtime config is missing,
 * or when the current browser doesn't support Firebase Messaging
 * (e.g. no Service Worker support).
 */
export const getFirebaseMessaging = (): Promise<Messaging | undefined> => {
  if (!hasHydrate || !app) return Promise.resolve(undefined);

  if (!messagingPromise) {
    messagingPromise = isSupported().then((supported) =>
      supported && app ? getMessaging(app) : undefined,
    );
  }

  return messagingPromise;
};

export { app };
